/* Copyright (c) 2020-2022 Nordcloud Oy or its affiliates. All Rights Reserved. */
import * as React from "react";
import { Option } from "@nordcloud/gnui/dist/components/dropdown/types";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { Button, Loader } from "@nordcloud/gnui";
import { useShowError } from "~/components/Error/useShowError";
import { SidebarDropdown } from "~/components/SidebarForm/inputs/SidebarDropdown";
import { SidebarForm } from "~/components/SidebarForm/SidebarForm";
import { SidebarFormButtons } from "~/components/SidebarForm/SidebarFormButtons";
import { showSuccess } from "~/components/Toast";

import { useAPIData } from "~/hooks/useAPIData";
import { CustomerService, RouteParams, ServiceOffering } from "~/models";
import { generateActionSuccessText } from "~/tools";
import { useAxios } from "~/tools/hooks/useAxios";

type Props = {
  assignedServices: CustomerService[];
  refetch: () => Promise<void>;
};

export default function ({ assignedServices, refetch }: Props) {
  const state = useParams<RouteParams>();
  const { customerId } = state;

  const [isSidebarOpen, setIsSidebarOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const { showError } = useShowError();
  const { Axios } = useAxios();

  const methods = useForm();
  const watcher = methods.watch();

  const [offerings, optionsLoading] = useAPIData<ServiceOffering[]>(
    "/service-offerings",
    (data) => data.serviceOfferings,
    []
  );

  React.useEffect(() => {
    if (watcher.service) {
      const offering = offerings.find((o) =>
        o.services.some((s) => s.id === watcher.service)
      );
      methods.setValue("offering", offering?.id);
    }
  }, [watcher.service]);

  const offeringOptions = offerings.map((o) => ({
    label: o.name,
    value: o.id,
  }));

  const assignedServiceIds = assignedServices.map((s) => s.id);
  const serviceOptions: Option[] = [];
  offerings
    .filter((o) => !watcher.offering || watcher.offering === o.id)
    .forEach((o) => {
      o.services
        .filter((s) => !assignedServiceIds.includes(s.id))
        .forEach((s) => {
          serviceOptions.push({ label: s.name, value: s.id });
        });
    });

  const onSubmit = async ({ service }: { service: string }) => {
    setIsLoading(true);
    try {
      await Axios().post(`/customers/${customerId}/service-offering`, {
        serviceId: service,
      });
      showSuccess(generateActionSuccessText(`Service`)()(`assigned`)());
      refetch();
    } catch (e) {
      showError(e.response);
    }
    setIsLoading(false);
    setIsSidebarOpen(false);
  };

  return (
    <>
      <Button
        onClick={() => setIsSidebarOpen(true)}
        css={{ marginLeft: "auto" }}
        icon="plus"
      >
        Assign service
      </Button>

      <SidebarForm
        title="Assign Service"
        open={isSidebarOpen}
        setOpen={setIsSidebarOpen}
        formMethods={methods}
        submit={onSubmit}
      >
        {optionsLoading ? (
          <div style={{ marginBottom: "1.5rem" }}>
            <Loader inContent />
          </div>
        ) : (
          <>
            <SidebarDropdown
              name="offering"
              options={offeringOptions}
              label="Service offering"
            />

            <SidebarDropdown
              name="service"
              options={serviceOptions}
              label="Service"
              required
            />
          </>
        )}

        <SidebarFormButtons
          onCancel={() => setIsSidebarOpen(false)}
          submitText="Assign"
          submitLoading={isLoading}
          submitEnabled={!!watcher.service}
        />
      </SidebarForm>
    </>
  );
}
